import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Form } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { dayOffApplicationsApiService } from '../../services/apiServices';
import {
  DayOffApplicationResponse,
  DayOffApplicationStatus,
  DayOffApplicationType,
} from '../../api-client';
import {
  getDayOffApplicationStatusLabel,
  getDayOffApplicationStatusBadgeVariant,
  getDayOffApplicationTypeLabel,
  dayOffStatusAtLeast,
} from '../utils/statusConverters';
import { currentUserService } from '../../services/currentUserService';
import { RoleName } from '../../api-client';

const DayOffApplicationsDetailPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<DayOffApplicationResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reverting, setReverting] = useState<boolean>(false);
  const [revertError, setRevertError] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState<boolean>(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [sendingNotification, setSendingNotification] = useState<boolean>(false);
  const [notificationError, setNotificationError] = useState<string | null>(null);
  const [notificationSuccess, setNotificationSuccess] = useState<boolean>(false);

  const [isAdminOrAccountant, setIsAdminOrAccountant] = useState<boolean>(false);

  const [downloadingOrder, setDownloadingOrder] = useState<boolean>(false);
  const [downloadingSignedOrder, setDownloadingSignedOrder] = useState<boolean>(false);
  const [orderFileError, setOrderFileError] = useState<string | null>(null);

  const [uploadingOrder, setUploadingOrder] = useState<boolean>(false);
  const [orderFile, setOrderFile] = useState<File | null>(null);
  const [uploadOrderError, setUploadOrderError] = useState<string | null>(null);
  const [uploadOrderSuccess, setUploadOrderSuccess] = useState<boolean>(false);

  const [uploadingSignedOrder, setUploadingSignedOrder] = useState<boolean>(false);
  const [signedOrderFile, setSignedOrderFile] = useState<File | null>(null);
  const [uploadSignedOrderError, setUploadSignedOrderError] = useState<string | null>(null);
  const [uploadSignedOrderSuccess, setUploadSignedOrderSuccess] = useState<boolean>(false);

  useEffect(() => {
    const checkRole = async () => {
      const hasRole = await currentUserService.hasAnyRole([RoleName.ADMIN, RoleName.ACCOUNTANT]);
      setIsAdminOrAccountant(hasRole);
    };
    checkRole();
  }, []);

  useEffect(() => {
    if (!applicationId) {
      navigate('/vacation/day-off');
      return;
    }

    const loadApplication = async () => {
      try {
        setLoading(true);
        const response = await dayOffApplicationsApiService.getApplication(applicationId);
        setApplication(response.data || null);
      } catch (err: any) {
        console.error('Ошибка при загрузке заявки:', err);
        setError(err.message || 'Ошибка при загрузке заявки');
      } finally {
        setLoading(false);
      }
    };

    loadApplication();
  }, [applicationId, navigate]);

  const handleRevertToOrderSigning = async () => {
    if (!applicationId) return;

    try {
      setReverting(true);
      setRevertError(null);
      const response = await dayOffApplicationsApiService.revertToOrderSigning(applicationId);
      setApplication(response.data || null);
    } catch (err: any) {
      console.error('Ошибка при возврате заявки на подписание приказа:', err);
      setRevertError(err.message || 'Ошибка при возврате заявки на подписание приказа');
    } finally {
      setReverting(false);
    }
  };

  const handleSendNotification = async () => {
    if (!applicationId) return;

    try {
      setSendingNotification(true);
      setNotificationError(null);
      setNotificationSuccess(false);

      await dayOffApplicationsApiService.notifyPlanned(applicationId);
      setNotificationSuccess(true);
    } catch (err: any) {
      console.error('Ошибка при отправке нотификации:', err);
      setNotificationError(err.message || 'Ошибка при отправке нотификации');
    } finally {
      setSendingNotification(false);
    }
  };

  const handleDownloadFile = async () => {
    if (!application || !applicationId) return;

    try {
      setDownloadingFile(true);
      setFileError(null);

      const response = await dayOffApplicationsApiService.getApplicationFileUrl(applicationId);
      const fileUrl = response.data?.file_url;

      if (!fileUrl) {
        throw new Error('Не удалось получить ссылку на файл');
      }

      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      console.error('Ошибка при получении ссылки на файл:', err);
      setFileError(err.message || 'Ошибка при получении ссылки на файл');
    } finally {
      setDownloadingFile(false);
    }
  };

  const handleDownloadOrder = async () => {
    if (!applicationId) return;

    try {
      setDownloadingOrder(true);
      setOrderFileError(null);

      const response = await dayOffApplicationsApiService.getOrderUrl(applicationId);
      const url = response.data?.file_url;

      if (!url) {
        throw new Error('Не удалось получить ссылку на приказ');
      }

      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      console.error('Ошибка при получении ссылки на приказ:', err);
      setOrderFileError(err.message || 'Ошибка при получении ссылки на приказ');
    } finally {
      setDownloadingOrder(false);
    }
  };

  const handleDownloadSignedOrder = async () => {
    if (!applicationId) return;

    try {
      setDownloadingSignedOrder(true);
      setOrderFileError(null);

      const response = await dayOffApplicationsApiService.getSignedOrderUrl(applicationId);
      const url = response.data?.file_url;

      if (!url) {
        throw new Error('Не удалось получить ссылку на подписанный приказ');
      }

      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      console.error('Ошибка при получении ссылки на подписанный приказ:', err);
      setOrderFileError(err.message || 'Ошибка при получении ссылки на подписанный приказ');
    } finally {
      setDownloadingSignedOrder(false);
    }
  };

  const handleUploadOrder = async () => {
    if (!applicationId || !orderFile) return;

    try {
      setUploadingOrder(true);
      setUploadOrderError(null);
      setUploadOrderSuccess(false);

      const tempFileResponse = await dayOffApplicationsApiService.uploadTempFile(orderFile);
      const fileUrl = tempFileResponse.data?.file_url;
      if (!fileUrl) {
        throw new Error('Не удалось загрузить файл');
      }

      const response = await dayOffApplicationsApiService.uploadOrder(
        applicationId,
        fileUrl,
        orderFile.name
      );
      setApplication(response.data || null);
      setOrderFile(null);
      setUploadOrderSuccess(true);
    } catch (err: any) {
      console.error('Ошибка при загрузке приказа:', err);
      setUploadOrderError(err.message || 'Ошибка при загрузке приказа');
    } finally {
      setUploadingOrder(false);
    }
  };

  const handleUploadSignedOrder = async () => {
    if (!applicationId || !signedOrderFile) return;

    try {
      setUploadingSignedOrder(true);
      setUploadSignedOrderError(null);
      setUploadSignedOrderSuccess(false);

      const tempFileResponse = await dayOffApplicationsApiService.uploadTempFile(signedOrderFile);
      const fileUrl = tempFileResponse.data?.file_url;
      if (!fileUrl) {
        throw new Error('Не удалось загрузить файл');
      }

      const response = await dayOffApplicationsApiService.uploadSignedOrder(
        applicationId,
        fileUrl,
        signedOrderFile.name
      );
      setApplication(response.data || null);
      setSignedOrderFile(null);
      setUploadSignedOrderSuccess(true);
    } catch (err: any) {
      console.error('Ошибка при загрузке подписанного приказа:', err);
      setUploadSignedOrderError(err.message || 'Ошибка при загрузке подписанного приказа');
    } finally {
      setUploadingSignedOrder(false);
    }
  };

  if (loading) {
    return (
      <Container
        fluid
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '50vh' }}
      >
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Загрузка заявки...</p>
        </div>
      </Container>
    );
  }

  if (error || !application) {
    return (
      <Container fluid>
        <Row className="mt-4">
          <Col>
            <Alert variant="danger">{error || 'Заявка не найдена'}</Alert>
            <Button variant="primary" onClick={() => navigate('/vacation/day-off')}>
              Вернуться к списку
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Заявка №{application.id}</h2>
            <Link to="/vacation/day-off">
              <Button variant="outline-secondary">Вернуться к списку</Button>
            </Link>
          </div>

          {revertError && <Alert variant="danger">{revertError}</Alert>}
          {fileError && <Alert variant="warning">{fileError}</Alert>}
          {orderFileError && <Alert variant="warning">{orderFileError}</Alert>}
          {uploadOrderError && <Alert variant="danger">{uploadOrderError}</Alert>}
          {uploadOrderSuccess && <Alert variant="success">Приказ успешно загружен</Alert>}
          {uploadSignedOrderError && <Alert variant="danger">{uploadSignedOrderError}</Alert>}
          {uploadSignedOrderSuccess && (
            <Alert variant="success">Подписанный приказ успешно загружен</Alert>
          )}
          {notificationError && <Alert variant="danger">{notificationError}</Alert>}
          {notificationSuccess && (
            <Alert variant="success">Нотификация успешно отправлена пользователю</Alert>
          )}

          <Card>
            <Card.Header>
              <Badge bg={getDayOffApplicationStatusBadgeVariant(application.status)}>
                {getDayOffApplicationStatusLabel(application.status)}
              </Badge>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h5>Основная информация</h5>
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th style={{ width: '40%' }}>ID заявки</th>
                        <td>{application.id}</td>
                      </tr>
                      <tr>
                        <th>Пользователь</th>
                        <td>
                          {application.user?.profile?.full_name && (
                            <div>
                              <strong>{application.user.profile.full_name}</strong>
                            </div>
                          )}
                          <div>
                            <strong>{application.user?.username || '—'}</strong>
                          </div>
                          <small className="text-muted">{application.user?.email || '—'}</small>
                        </td>
                      </tr>
                      <tr>
                        <th>Тип выходного</th>
                        <td>
                          <Badge
                            bg={
                              application.type === DayOffApplicationType.PAID ? 'info' : 'warning'
                            }
                          >
                            {getDayOffApplicationTypeLabel(application.type)}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <th>Статус</th>
                        <td>
                          <Badge bg={getDayOffApplicationStatusBadgeVariant(application.status)}>
                            {getDayOffApplicationStatusLabel(application.status)}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <th>Дата создания</th>
                        <td>{new Date(application.created_at).toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>

                <Col md={6}>
                  <h5>Причина</h5>
                  <p className="text-muted">{application.reason || 'Причина не указана'}</p>

                  <hr />

                  <h5>Файлы заявки</h5>
                  <Button
                    variant="primary"
                    onClick={handleDownloadFile}
                    disabled={downloadingFile}
                    className="mb-3"
                  >
                    {downloadingFile ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Открытие...
                      </>
                    ) : (
                      'Открыть файл заявки'
                    )}
                  </Button>

                  {dayOffStatusAtLeast(
                    application.status,
                    DayOffApplicationStatus.ORDER_SIGNING
                  ) && (
                    <div className="mt-2">
                      <Button
                        variant="outline-primary"
                        onClick={handleDownloadOrder}
                        disabled={downloadingOrder}
                        size="sm"
                      >
                        {downloadingOrder ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Загрузка...
                          </>
                        ) : (
                          'Скачать приказ'
                        )}
                      </Button>
                    </div>
                  )}

                  {dayOffStatusAtLeast(
                    application.status,
                    DayOffApplicationStatus.ORDER_SIGNED
                  ) && (
                    <div className="mt-2">
                      <Button
                        variant="outline-primary"
                        onClick={handleDownloadSignedOrder}
                        disabled={downloadingSignedOrder}
                        size="sm"
                      >
                        {downloadingSignedOrder ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Загрузка...
                          </>
                        ) : (
                          'Скачать подписанный приказ'
                        )}
                      </Button>
                    </div>
                  )}

                  {isAdminOrAccountant && (
                    <>
                      <hr />

                      <h5>Загрузка приказа</h5>
                      {(application.status === DayOffApplicationStatus.PENDING ||
                        application.status === DayOffApplicationStatus.ORDER_SIGNING) && (
                        <div className="mb-3">
                          {application.status === DayOffApplicationStatus.ORDER_SIGNING && (
                            <Alert variant="warning" className="py-2 px-3 small mb-2">
                              У заявки уже есть приказ. При загрузке нового приказа существующий
                              будет заменён.
                            </Alert>
                          )}
                          <Form.Group>
                            <Form.Control
                              type="file"
                              accept=".pdf,.doc,.docx"
                              onChange={(e) => {
                                const target = e.target as HTMLInputElement;
                                setOrderFile(target.files?.[0] || null);
                              }}
                              disabled={uploadingOrder}
                              size="sm"
                            />
                          </Form.Group>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="mt-2"
                            onClick={handleUploadOrder}
                            disabled={uploadingOrder || !orderFile}
                          >
                            {uploadingOrder ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Загрузка...
                              </>
                            ) : (
                              'Загрузить приказ'
                            )}
                          </Button>
                        </div>
                      )}

                      <h5>Загрузка подписанного приказа</h5>
                      {(application.status === DayOffApplicationStatus.ORDER_SIGNING ||
                        application.status === DayOffApplicationStatus.ORDER_SIGNED) && (
                        <div className="mb-3">
                          {application.status === DayOffApplicationStatus.ORDER_SIGNED && (
                            <Alert variant="warning" className="py-2 px-3 small mb-2">
                              У заявки уже есть подписанный приказ. При загрузке нового подписанного
                              приказа существующий будет заменён.
                            </Alert>
                          )}
                          <Form.Group>
                            <Form.Control
                              type="file"
                              accept=".pdf,.doc,.docx"
                              onChange={(e) => {
                                const target = e.target as HTMLInputElement;
                                setSignedOrderFile(target.files?.[0] || null);
                              }}
                              disabled={uploadingSignedOrder}
                              size="sm"
                            />
                          </Form.Group>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="mt-2"
                            onClick={handleUploadSignedOrder}
                            disabled={uploadingSignedOrder || !signedOrderFile}
                          >
                            {uploadingSignedOrder ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Загрузка...
                              </>
                            ) : (
                              'Загрузить подписанный приказ'
                            )}
                          </Button>
                        </div>
                      )}
                    </>
                  )}

                  <hr />

                  <h5>Действия со статусом</h5>
                  {(application.status === DayOffApplicationStatus.ORDER_SIGNED ||
                    application.status === DayOffApplicationStatus.CONFIRMED_BY_USER) && (
                    <div className="mb-3">
                      <p className="text-muted small">
                        Вернуть заявку в статус «Ожидает подписания приказа»
                      </p>
                      <Button
                        variant="warning"
                        onClick={handleRevertToOrderSigning}
                        disabled={reverting}
                        size="sm"
                      >
                        {reverting ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Возврат...
                          </>
                        ) : (
                          'Вернуть к подписанию приказа'
                        )}
                      </Button>
                    </div>
                  )}

                  {application.status === DayOffApplicationStatus.ORDER_SIGNED && (
                    <div className="mb-3">
                      <p className="text-muted small">
                        Отправить пользователю уведомление о необходимости принять решение по заявке
                      </p>
                      <Button
                        variant="primary"
                        onClick={handleSendNotification}
                        disabled={sendingNotification}
                        size="sm"
                      >
                        {sendingNotification ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Отправка...
                          </>
                        ) : (
                          'Отправить нотификацию'
                        )}
                      </Button>
                    </div>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default DayOffApplicationsDetailPage;
